// src/shared/hooks/useVoiceCapture.ts
// ────────────────────────────────────────────────────────────────────────────
// DRY reusable voice capture hook — record audio via MediaRecorder (Opus/WebM),
// send to Google Cloud STT via existing BFF pipeline, return transcribed text.
//
// Consumers: Agent SKU Builder, SpeechToTextPanel, PACDaemon CLI, future modules.
// Architecture: Hook-Owns-All-State — components never touch recording logic.
//
// Recording pipeline:
//   Browser MediaRecorder (audio/webm;codecs=opus)
//   → Blob → File
//   → transcribeSttFile() (multipart upload to BFF)
//   → Google Cloud STT (sync ≤1MB, LRO for larger)
//   → Transcript + metadata returned
//
// Language strategy: Spanish-first with English fallback via alternativeLanguageCodes.
// ────────────────────────────────────────────────────────────────────────────
import { useState, useCallback, useRef, useEffect } from 'react';
import {
  transcribeSttFile,
  type SttTranscribeResponse,
  type SttEncoding,
  type SttModel,
} from '@/features/services/google-apis/api/sttApi';

// ── Types ────────────────────────────────────────────────────────────────

export type VoiceCapturePhase =
  | 'idle'
  | 'recording'
  | 'processing'
  | 'done'
  | 'error';

export interface VoiceCaptureConfig {
  /** Primary BCP-47 language (default: es-ES) */
  languageCode: string;
  /** Fallback languages for multi-language detection */
  alternativeLanguageCodes: string[];
  /** STT model (default: latest_short for voice memos) */
  model: SttModel;
  /** Enable word-level time offsets */
  enableWordTimeOffsets: boolean;
  /** Enable per-word confidence scores */
  enableWordConfidence: boolean;
  /** Enable automatic punctuation */
  enableAutomaticPunctuation: boolean;
  /** Max recording duration in seconds (safety limit, default: 120) */
  maxDurationSec: number;
}

export interface VoiceCaptureResult {
  transcript: string;
  confidence: number;
  wordCount: number;
  detectedLanguage: string | null;
  words: SttTranscribeResponse['words'];
  alternatives: SttTranscribeResponse['alternatives'];
  durationMs: number;
  /** Raw audio blob captured from MediaRecorder (Opus/WebM) */
  audioBlob: Blob;
  /** Raw STT response for advanced consumers */
  raw: SttTranscribeResponse;
}

const DEFAULT_CONFIG: VoiceCaptureConfig = {
  languageCode: 'es-US',
  alternativeLanguageCodes: ['en-US', 'es-ES'],
  model: 'latest_short',
  enableWordTimeOffsets: false,
  enableWordConfidence: false,
  enableAutomaticPunctuation: true,
  maxDurationSec: 300,
};

/** Duration threshold (seconds) to switch from latest_short to latest_long. */
const LONG_MODEL_THRESHOLD_SEC = 55;

/** Pick the best STT model based on recording duration. */
function autoModel(baseCfg: VoiceCaptureConfig, durationSec: number): SttModel {
  // Explicit override by consumer — honour it without auto-switching.
  if (baseCfg.model !== 'latest_short' && baseCfg.model !== 'latest_long') {
    return baseCfg.model;
  }
  return durationSec >= LONG_MODEL_THRESHOLD_SEC ? 'latest_long' : 'latest_short';
}

// ── Hook ─────────────────────────────────────────────────────────────────

export function useVoiceCapture(configOverrides?: Partial<VoiceCaptureConfig>) {
  const config = { ...DEFAULT_CONFIG, ...configOverrides };

  const [phase, setPhase]           = useState<VoiceCapturePhase>('idle');
  const [result, setResult]         = useState<VoiceCaptureResult | null>(null);
  const [error, setError]           = useState<string | null>(null);
  const [elapsedSec, setElapsedSec] = useState(0);

  const recorderRef   = useRef<MediaRecorder | null>(null);
  const streamRef     = useRef<MediaStream | null>(null);
  const chunksRef     = useRef<Blob[]>([]);
  const timerRef      = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef  = useRef<number>(0);
  const mountedRef    = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopRecording();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Internal: release hardware ────────────────────────────────────────
  const releaseMedia = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch { /* already stopped */ }
    }
    recorderRef.current = null;

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // ── Internal: process recorded audio → STT ────────────────────────────
  const processAudio = useCallback(async (blob: Blob) => {
    if (!mountedRef.current) return;
    setPhase('processing');

    const recordingDuration = Date.now() - startTimeRef.current;
    const durationSec       = Math.round(recordingDuration / 1000);

    try {
      const file = new File([blob], 'voice-capture.webm', { type: 'audio/webm' });

      // Auto-select model based on actual recording duration
      const resolvedModel = autoModel(config, durationSec);

      const sttParams: Parameters<typeof transcribeSttFile>[1] = {
        languageCode: config.languageCode,
        model: resolvedModel,
        enableAutomaticPunctuation: config.enableAutomaticPunctuation,
        enableWordTimeOffsets: config.enableWordTimeOffsets,
        enableWordConfidence: config.enableWordConfidence,
        // Tell the BFF the real recording duration so it can force LRO
        // routing for audio >= 55 s regardless of compressed byte size.
        durationHintSec: durationSec,
      };

      if (config.alternativeLanguageCodes.length > 0) {
        sttParams.alternativeLanguageCodes = config.alternativeLanguageCodes;
      }

      let response = await transcribeSttFile(file, sttParams);

      if (!mountedRef.current) return;

      // ── Retry with swapped language on empty transcript ───────────
      // If primary language produced nothing, retry once with the first
      // alternative as primary — handles mixed-language dictation where
      // the user starts in a different language than expected.
      if (
        (!response.ok || !response.transcript) &&
        config.alternativeLanguageCodes.length > 0
      ) {
        const swappedPrimary = config.alternativeLanguageCodes[0];
        const swappedAlts    = [config.languageCode, ...config.alternativeLanguageCodes.slice(1)];

        response = await transcribeSttFile(file, {
          ...sttParams,
          languageCode:            swappedPrimary,
          alternativeLanguageCodes: swappedAlts,
        });

        if (!mountedRef.current) return;
      }

      if (!response.ok || !response.transcript) {
        throw new Error(
          'No speech detected — ensure your microphone is working and try speaking louder or more clearly.',
        );
      }

      setResult({
        transcript:       response.transcript,
        confidence:       response.confidence ?? 0,
        wordCount:        response.wordCount ?? 0,
        detectedLanguage: response.detectedLanguage ?? null,
        words:            response.words ?? [],
        alternatives:     response.alternatives ?? [],
        durationMs:       recordingDuration,
        audioBlob:        blob,
        raw:              response,
      });
      setPhase('done');
      setError(null);
    } catch (err: any) {
      if (!mountedRef.current) return;
      setError(err?.message || 'Voice transcription failed');
      setPhase('error');
    }
  }, [config]);

  // ── Start recording ───────────────────────────────────────────────────
  const startRecording = useCallback(async () => {
    // Reset previous state
    setResult(null);
    setError(null);
    setElapsedSec(0);
    chunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      if (!mountedRef.current) {
        stream.getTracks().forEach(t => t.stop());
        return;
      }

      streamRef.current = stream;

      // Prefer Opus/WebM — natively supported by Chrome, Edge, Firefox.
      // The STT backend auto-detects WEBM_OPUS via magic-byte sniffing.
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const recorder = new MediaRecorder(stream, { mimeType });
      recorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        chunksRef.current = [];
        processAudio(blob);
      };

      recorder.onerror = () => {
        if (!mountedRef.current) return;
        releaseMedia();
        setError('Recording failed — microphone error');
        setPhase('error');
      };

      recorder.start(250); // collect chunks every 250ms
      startTimeRef.current = Date.now();
      setPhase('recording');

      // Elapsed timer for UI feedback
      timerRef.current = setInterval(() => {
        if (!mountedRef.current) return;
        const sec = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setElapsedSec(sec);
        // Safety limit
        if (sec >= config.maxDurationSec) {
          stopRecording();
        }
      }, 500);

    } catch (err: any) {
      if (!mountedRef.current) return;
      if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
        setError('Microphone permission denied — please allow access');
      } else if (err?.name === 'NotFoundError') {
        setError('No microphone found — connect an audio input device');
      } else {
        setError(err?.message || 'Failed to start recording');
      }
      setPhase('error');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, processAudio, releaseMedia]);

  // ── Stop recording (triggers onstop → processAudio) ───────────────────
  const stopRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.stop();
    }
    // Release mic immediately — but keep recorder ref for onstop callback
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // ── Toggle (convenience for single-button UX) ─────────────────────────
  const toggle = useCallback(() => {
    if (phase === 'recording') {
      stopRecording();
    } else if (phase === 'idle' || phase === 'done' || phase === 'error') {
      startRecording();
    }
  }, [phase, startRecording, stopRecording]);

  // ── Reset to idle ─────────────────────────────────────────────────────
  const reset = useCallback(() => {
    releaseMedia();
    setPhase('idle');
    setResult(null);
    setError(null);
    setElapsedSec(0);
    chunksRef.current = [];
  }, [releaseMedia]);

  return {
    // State
    phase,
    isRecording:  phase === 'recording',
    isProcessing: phase === 'processing',
    result,
    error,
    elapsedSec,

    // Actions
    startRecording,
    stopRecording,
    toggle,
    reset,
  };
}
